import { contentHashForVersionedArtifact, LEGACY_HASH_ALGORITHM } from './index-builder/content-hash.js'
import { ZodError } from 'zod'

import { DocBridgeConfigV1Schema, type DocBridgeConfigV1 } from './config/schema.js'
import {
  AgentHandoffLegacySchema,
  AgentSearchV1Schema,
  normalizeAgentHandoff,
  type AgentHandoffV1,
  type AgentSearchV1,
} from './schemas/agent-handoff.js'
import { DocBridgeIndexV1Schema, type DocBridgeIndexV1 } from './schemas/doc-bridge-index.js'
import {
  MemoryCandidateV1Schema,
  type MemoryCandidateV1,
} from './schemas/memory-candidate.js'
import {
  AgentProposalV1Schema,
  DiscoverySnapshotV1Schema,
  FixProposalV1Schema,
  ReconciliationReportV1Schema,
  WorkflowRunV1Schema,
  type AgentProposalV1,
  type DiscoverySnapshotV1,
  type FixProposalV1,
  type ReconciliationReportV1,
  type WorkflowRunV1,
} from './schemas/knowledge.js'

export type ParseIssue = {
  readonly path: string
  readonly message: string
}

export type ParseResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly issues: readonly ParseIssue[] }

const zodMessage = (issue: ZodError['issues'][number]): string => {
  if (issue.code === 'invalid_type' && issue.message.endsWith('received undefined')) return 'Required'
  if (issue.code === 'invalid_value' && 'values' in issue) return 'Invalid enum value'
  return issue.message
}

const zodIssues = (error: ZodError): readonly ParseIssue[] =>
  error.issues.map((issue) => ({
    path: issue.path.join('.') || '(root)',
    message: zodMessage(issue),
  }))

export const safeParseAgentHandoff = (input: unknown): ParseResult<AgentHandoffV1> => {
  const legacy = AgentHandoffLegacySchema.safeParse(input)
  if (!legacy.success) return { ok: false, issues: zodIssues(legacy.error) }
  return { ok: true, value: normalizeAgentHandoff(legacy.data, { includeCaveats: true }) }
}

export const parseAgentHandoff = (input: unknown): AgentHandoffV1 => {
  const result = safeParseAgentHandoff(input)
  if (!result.ok) {
    throw new Error(
      `Invalid AgentHandoff:\n${result.issues.map((i) => `  - ${i.path}: ${i.message}`).join('\n')}`,
    )
  }
  return result.value
}

export const parseAgentSearch = (input: unknown): AgentSearchV1 => AgentSearchV1Schema.parse(input)

export const parseDocBridgeIndex = (input: unknown): DocBridgeIndexV1 =>
  DocBridgeIndexV1Schema.parse(input)

export const parseMemoryCandidate = (input: unknown): MemoryCandidateV1 =>
  MemoryCandidateV1Schema.parse(input)

const verifyKnowledgeHash = <T extends { contentHash: string; contentHashAlgo: string }>(artifact: T): T => {
  if (artifact.contentHash !== contentHashForVersionedArtifact(artifact)) throw new Error(`Invalid artifact content hash under ${artifact.contentHashAlgo}. Explicitly regenerate with a compatible doc-bridge version.`)
  return artifact
}

export const parseDiscoverySnapshot = (input: unknown): DiscoverySnapshotV1 => {
  const snapshot = verifyKnowledgeHash(DiscoverySnapshotV1Schema.parse(input))
  if (snapshot.contentHashAlgo === LEGACY_HASH_ALGORITHM) throw new Error('Legacy snapshot is migration-only. Regenerate with ak-docs discover before analysis.')
  return snapshot
}

export const parseReconciliationReport = (input: unknown): ReconciliationReportV1 => {
  const report = verifyKnowledgeHash(ReconciliationReportV1Schema.parse(input))
  if (report.contentHashAlgo === LEGACY_HASH_ALGORITHM) throw new Error('Legacy report is migration-only. Regenerate with ak-docs reconcile before analysis.')
  return report
}

export const parseWorkflowRun = (input: unknown): WorkflowRunV1 => WorkflowRunV1Schema.parse(input)

export const parseAgentProposal = (input: unknown): AgentProposalV1 => AgentProposalV1Schema.parse(input)

export const parseFixProposal = (input: unknown): FixProposalV1 => FixProposalV1Schema.parse(input)

export const parseDocBridgeConfig = (input: unknown): DocBridgeConfigV1 => {
  const result = DocBridgeConfigV1Schema.safeParse(input)
  if (!result.success) {
    throw new Error(
      `Invalid doc-bridge config:\n${zodIssues(result.error).map((i) => `  - ${i.path}: ${i.message}`).join('\n')}`,
    )
  }
  return result.data
}
